import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, X, Calendar, Users, FileText, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const ClubMeetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    club_name: '',
    meeting_date: '',
    spoken_to: '',
    notes: ''
  });

  useEffect(() => {
    fetchMeetings();
    fetchClubs();
  }, []);

  const fetchMeetings = async () => {
    try {
      const response = await api.get('/club-meetings');
      setMeetings(response.data);
    } catch (error) {
      toast.error('Failed to load meetings');
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await api.get('/clubs');
      setClubs(response.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to load clubs');
    }
  };

  const resetForm = () => {
    setFormData({ club_name: '', meeting_date: '', spoken_to: '', notes: '' });
    setEditingMeeting(null);
    setShowForm(false);
  };

  const handleEdit = (meeting) => {
    setEditingMeeting(meeting);
    setFormData({
      club_name: meeting.club_name || '',
      meeting_date: meeting.meeting_date || '',
      spoken_to: meeting.spoken_to || '',
      notes: meeting.notes || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMeeting) {
        await api.put(`/club-meetings/${editingMeeting.id}`, formData);
        toast.success('Meeting updated');
      } else {
        await api.post('/club-meetings', formData);
        toast.success('Meeting added');
      }
      resetForm();
      fetchMeetings();
    } catch (error) {
      toast.error(editingMeeting ? 'Failed to update meeting' : 'Failed to add meeting');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await api.delete(`/club-meetings/${id}`);
      toast.success('Meeting deleted');
      fetchMeetings();
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  const filteredMeetings = useMemo(() => meetings.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return m.club_name.toLowerCase().includes(q) ||
           m.spoken_to.toLowerCase().includes(q) ||
           (m.notes || '').toLowerCase().includes(q);
  }), [meetings, searchQuery]);

  // Group meetings by club
  const { meetingsByClub, sortedClubs } = useMemo(() => {
    const grouped = {};
    filteredMeetings.forEach(m => {
      if (!grouped[m.club_name]) grouped[m.club_name] = [];
      grouped[m.club_name].push(m);
    });
    return { meetingsByClub: grouped, sortedClubs: Object.keys(grouped).sort((a, b) => a.localeCompare(b)) };
  }, [filteredMeetings]);

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">Club Meetings</h1>
              <p className="text-lg text-zinc-300">{meetings.length} meeting{meetings.length !== 1 ? 's' : ''} recorded</p>
            </div>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              data-testid="add-meeting-button"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3"
            >
              <Plus className="w-5 h-5" />
              Add Meeting
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Search */}
        <div className="glass-card p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by club, person, or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="meeting-search-input"
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md pl-10 pr-4 py-3 text-white outline-none text-sm"
            />
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold uppercase text-white">
                {editingMeeting ? 'Edit Meeting' : 'New Meeting'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Club Name *</label>
                  <select
                    required
                    value={formData.club_name}
                    onChange={(e) => setFormData({ ...formData, club_name: e.target.value })}
                    data-testid="meeting-club-name-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                  >
                    <option value="">Select club</option>
                    {clubs.map(club => (
                      <option key={club.id} value={club.name}>{club.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.meeting_date}
                    onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                    data-testid="meeting-date-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Spoken To *</label>
                  <input
                    type="text"
                    required
                    placeholder="Who did you speak to?"
                    value={formData.spoken_to}
                    onChange={(e) => setFormData({ ...formData, spoken_to: e.target.value })}
                    data-testid="meeting-spoken-to-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Meeting Notes</label>
                  <textarea
                    rows="3"
                    placeholder="Notes from the meeting..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="meeting-notes-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" data-testid="save-meeting-button" className="bg-white text-black hover:bg-zinc-200 font-bold uppercase px-6 py-2 rounded-sm">
                  {editingMeeting ? 'Update Meeting' : 'Save Meeting'}
                </button>
                <button type="button" onClick={resetForm} className="bg-white/10 text-white hover:bg-white/20 px-6 py-2 rounded-sm">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Meetings grouped by club */}
        {sortedClubs.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">{searchQuery ? 'No meetings match your search' : 'No club meetings yet. Add your first meeting!'}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedClubs.map(clubName => (
              <div key={clubName} className="glass-card overflow-hidden" data-testid={`club-meeting-panel-${clubName.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="p-4 bg-white/5 border-b border-white/5 flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Users className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold uppercase tracking-tight text-white">{clubName}</h3>
                    <p className="text-xs text-zinc-400">{meetingsByClub[clubName].length} meeting{meetingsByClub[clubName].length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="divide-y divide-white/5">
                  {meetingsByClub[clubName].sort((a, b) => new Date(b.meeting_date) - new Date(a.meeting_date)).map(meeting => (
                    <div key={meeting.id} className="p-4 hover:bg-white/[0.02] transition-colors" data-testid={`meeting-row-${meeting.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-white" strokeWidth={1.5} />
                              <span className="text-sm font-bold text-white">{formatDate(meeting.meeting_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-purple-400" strokeWidth={1.5} />
                              <span className="text-sm text-zinc-300">{meeting.spoken_to}</span>
                            </div>
                          </div>
                          {meeting.notes && (
                            <div className="flex items-start gap-2">
                              <FileText className="w-3.5 h-3.5 text-zinc-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                              <p className="text-sm text-zinc-400">{meeting.notes}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleEdit(meeting)}
                            data-testid={`edit-meeting-${meeting.id}`}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5 text-zinc-400 hover:text-white" />
                          </button>
                          <button
                            onClick={() => handleDelete(meeting.id)}
                            data-testid={`delete-meeting-${meeting.id}`}
                            className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-zinc-400 hover:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubMeetings;
